import {
  collection,
  getDocs,
  addDoc,
  doc,
  setDoc,
  getDoc,
  query,
  where,
  updateDoc
} from "firebase/firestore";

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile
} from "firebase/auth";

import { db, auth } from "../firebase/config";

export interface User {
  id?: string;
  email: string;
  name: string;
  role: string | null;
  createdAt: string;
  isActive: boolean;
  authUid?: string;
  orgId?: string | null;
  status?: string;
  updatedAt?: string;
  hasPassword?: boolean;
}

export interface Organization {
  id?: string;
  name: string;
  domain: string;
  primaryColor: string;
  adminCount: number;
  candidateCount: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface LoginResult {
  user: {
    uid: string;
    email: string;
    name: string;
    role: string | null;
    organizationId: string | null;
    hasOrganization: boolean;
    userDocId: string;
  };
}

export interface CreateAccountResult {
  success: boolean;
  error?: string;
  user?: {
    uid: string;
    email: string;
    name: string;
    role: null;
    hasOrganization: boolean;
  };
}

export interface CreateOrgResult {
  success: boolean;
  error?: string;
  orgId?: string;
  organization?: Organization;
}

// Create default super admin
export const createDefaultSuperAdmin = async () => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("role", "==", "super_admin"));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) return;

    const email = "superadmin@psychoassess.com";
    const password = "SuperAdmin@123";

    // Create Firebase Auth user
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: "Super Admin" });

    // Create Firestore user profile
    await addDoc(usersRef, {
      email,
      name: "Super Admin",
      role: "super_admin",
      createdAt: new Date().toISOString(),
      isActive: true,
      status: "active",
      authUid: cred.user.uid,
      orgId: null,
      hasPassword: true,
      updatedAt: new Date().toISOString()
    });

    console.log("Default Super Admin Created - Email: superadmin@psychoassess.com, Password: SuperAdmin@123");
  } catch (err) {
    console.log("Admin already exists or error:", err);
  }
};

// Create new user account
export const createUserAccount = async (email: string, password: string, fullName: string): Promise<CreateAccountResult> => {
  try {
    // Check if email already exists in Firestore
    const usersRef = collection(db, "users");
    const emailQuery = query(usersRef, where("email", "==", email));
    const emailSnapshot = await getDocs(emailQuery);
    
    if (!emailSnapshot.empty) {
      return { success: false, error: "Email already registered" };
    }

    // Create Firebase Auth user
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: fullName });

    // Create Firestore user profile
    const userData = {
      email,
      name: fullName,
      role: null,
      status: "pending",
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      authUid: cred.user.uid,
      orgId: null,
      hasPassword: true
    };

    await addDoc(usersRef, userData);

    return {
      success: true,
      user: {
        uid: cred.user.uid,
        email: email,
        name: fullName,
        role: null,
        hasOrganization: false
      }
    };
  } catch (err: any) {
    console.error(err);
    let errorMessage = "Failed to create account";
    if (err.code === "auth/email-already-in-use") {
      errorMessage = "Email already in use";
    } else if (err.code === "auth/invalid-email") {
      errorMessage = "Invalid email address";
    } else if (err.code === "auth/weak-password") {
      errorMessage = "Password must be at least 6 characters";
    }
    return { success: false, error: errorMessage };
  }
};

// Login user
export const loginUser = async (email: string, password: string): Promise<LoginResult | string | null> => {
  try {
    // Login using Firebase Auth
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const uid = cred.user.uid;

    // Get Firestore user profile
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("authUid", "==", uid));
    const snapshot = await getDocs(q);

    if (snapshot.empty) return null;

    const docSnap = snapshot.docs[0];
    const userData = docSnap.data() as User;
    
    // Check if user has organization
    const hasOrganization = !!(userData.orgId && userData.orgId !== null);
    
    return {
      user: {
        uid: uid,
        email: userData.email,
        name: userData.name,
        role: userData.role || null,
        organizationId: userData.orgId || null,
        hasOrganization: hasOrganization,
        userDocId: docSnap.id
      }
    };
  } catch (err: any) {
    if (err.code === "auth/wrong-password") return "wrong_password";
    if (err.code === "auth/user-not-found") return null;
    if (err.code === "auth/invalid-credential") return "wrong_password";
    console.error(err);
    return null;
  }
};

// Create organization
export const createOrganization = async (userId: string, userDocId: string, orgData: {
  name: string;
  domain: string;
  plan: string;
  primaryColor: string;
}): Promise<CreateOrgResult> => {
  try {
    // Generate unique organization ID
    const orgId = Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    
    // Create organization document
    const organizationRef = doc(db, "organizations", orgId);
    const organization: Organization = {
      id: orgId,
      name: orgData.name,
      domain: orgData.domain,
      primaryColor: orgData.primaryColor,
      adminCount: 1,
      candidateCount: 0,
      status: "active",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    await setDoc(organizationRef, organization);
    
    // Update user with organization ID and role
    const userRef = doc(db, "users", userDocId);
    await updateDoc(userRef, {
      orgId: orgId,
      role: "org_admin",
      status: "active",
      updatedAt: new Date().toISOString()
    });
    
    // Create assessment templates structure for the organization
    const assessmentTemplatesRef = doc(db, "assessmentTemplates", orgId);
    await setDoc(assessmentTemplatesRef, {
      organizations: {},
      questionBanks: {},
      questions: {},
      scales: {},
      scoringRules: {},
      sections: {},
      traitCategories: {},
      traits: {},
      users: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    return { 
      success: true, 
      orgId: orgId,
      organization: organization
    };
  } catch (err: any) {
    console.error("Error creating organization:", err);
    return { success: false, error: err.message };
  }
};

// Get user by authUid
export const getUserByAuthUid = async (authUid: string) => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("authUid", "==", authUid));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    const docSnap = snapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() as User };
  } catch (err) {
    console.error(err);
    return null;
  }
};

// Check if user has organization
export const checkUserOrganization = async (userId: string) => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("authUid", "==", userId));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return false;
    
    const userData = snapshot.docs[0].data() as User;
    return !!(userData.orgId && userData.orgId !== null);
  } catch (err) {
    console.error(err);
    return false;
  }
};

// Get organization by ID
export const getOrganizationById = async (orgId: string) => {
  try {
    const orgRef = doc(db, "organizations", orgId);
    const orgSnap = await getDoc(orgRef);
    
    if (!orgSnap.exists()) return null;
    
    return { id: orgSnap.id, ...orgSnap.data() as Organization };
  } catch (err) {
    console.error(err);
    return null;
  }
};

// Update user role
export const updateUserRole = async (userDocId: string, role: string) => {
  try {
    const userRef = doc(db, "users", userDocId);
    await updateDoc(userRef, {
      role: role,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (err: any) {
    console.error(err);
    return { success: false, error: err.message };
  }
};